import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Col,
  Row,
  Container,
  Card,
  CardBody,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  ListGroup,
  ListGroupItem,
  CardHeader,
  Spinner
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../constants";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const View = () => {
  const { id } = useParams();
  const toast = React.useRef(null);

  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);

  const [loading, setLoading] = useState(true);
  const [milestoneLoading, setMilestoneLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);

  // Modal states
  const [milestoneModalOpen, setMilestoneModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Form states
  const [milestoneFormData, setMilestoneFormData] = useState({
    title: "",
    description: "",
    start_date: "",
    due_date: "",
    status: "planning"
  });

  const [taskFormData, setTaskFormData] = useState({
    title: "",
    description: "",
    status: "not_started",
    priority: "medium",
    assigned_to: "",
    due_date_time: ""
  });

  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [milestoneToDelete, setMilestoneToDelete] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [isTaskDelete, setIsTaskDelete] = useState(false);

  const breadcrumbItems = [
    { title: "Projects", link: "/projects" },
    { title: "Project Overview", link: "#!" },
  ];

  const fetchProjectDetails = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/project/view/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.status === true) {
        setProject(response.data.data);
      } else {
        showToast("error", "Error", "Failed to fetch project details");
      }
    } catch (error) {
      showToast("error", "Error", "Failed to fetch project details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchMilestones = React.useCallback(async () => {
    if (!project) return;
    setMilestoneLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/milestone/list/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.status === true) {
        setMilestones(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching milestones:", error);
      // Don't show toast for empty lists or minor errors to avoid clutter
    } finally {
      setMilestoneLoading(false);
    }
  }, [id, project]);

  const fetchTasks = React.useCallback(async () => {
    if (!project) return;
    setTaskLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/task/list/project/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.status === true) {
        setTasks(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setTaskLoading(false);
    }
  }, [id, project]);

  const fetchStaff = React.useCallback(async () => {
    setStaffLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/staff/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.status === true) {
        setStaff(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
    } finally {
      setStaffLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!project) {
      fetchProjectDetails();
    } else {
      fetchMilestones();
      fetchTasks();
      fetchStaff();
    }
  }, [id, project, fetchProjectDetails, fetchMilestones, fetchTasks, fetchStaff]);

  const showToast = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail, life: 3000 });
  };

  // Milestone Handlers
  const handleMilestoneInputChange = (e) => {
    setMilestoneFormData({ ...milestoneFormData, [e.target.name]: e.target.value });
  };

  const handleMilestoneDescriptionChange = (value) => {
    setMilestoneFormData({ ...milestoneFormData, description: value });
  };

  const handleMilestoneSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      const payload = { ...milestoneFormData, project_id: id };
      let response;
      if (isEditing) {
        response = await axios.post(`${API_BASE_URL}/milestone/update/${currentEditId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        response = await axios.post(`${API_BASE_URL}/milestone/add`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (response.data && response.data.status === true) {
        showToast("success", "Success", isEditing ? "Milestone updated" : "Milestone created");
        setMilestoneModalOpen(false);
        fetchMilestones();
        resetMilestoneForm();
      } else {
        showToast("error", "Error", response.data.message || "Operation failed");
      }
    } catch (error) {
      showToast("error", "Error", "Operation failed");
    }
  };

  const editMilestone = (milestone) => {
    setIsEditing(true);
    setCurrentEditId(milestone.id);
    setMilestoneFormData({
      title: milestone.title,
      description: milestone.description,
      start_date: milestone.start_date,
      due_date: milestone.due_date,
      status: milestone.status
    });
    setMilestoneModalOpen(true);
  };

  const confirmDeleteMilestone = (milestone) => {
    setMilestoneToDelete(milestone);
    setIsTaskDelete(false);
    setDeleteModalOpen(true);
  };

  const resetMilestoneForm = () => {
    setMilestoneFormData({
      title: "",
      description: "",
      start_date: "",
      due_date: "",
      status: "planning"
    });
    setIsEditing(false);
    setCurrentEditId(null);
  };

  // Task Handlers
  const handleTaskInputChange = (e) => {
    setTaskFormData({ ...taskFormData, [e.target.name]: e.target.value });
  };

  const handleTaskDescriptionChange = (value) => {
    setTaskFormData({ ...taskFormData, description: value });
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      const payload = { ...taskFormData, project_id: id };
      let response;
      if (isEditing) {
        response = await axios.post(`${API_BASE_URL}/task/update/${currentEditId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        response = await axios.post(`${API_BASE_URL}/task/add`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (response.data && response.data.status === true) {
        showToast("success", "Success", isEditing ? "Task updated" : "Task created");
        setTaskModalOpen(false);
        fetchTasks();
        resetTaskForm();
      } else {
        showToast("error", "Error", response.data.message || "Operation failed");
      }
    } catch (error) {
      showToast("error", "Error", "Operation failed");
    }
  };

  const editTask = (task) => {
    setIsEditing(true);
    setCurrentEditId(task.id);
    setTaskFormData({
      title: task.title,
      description: task.description,
      status: task.status,
      priority: task.priority,
      assigned_to: task.assigned_to,
      due_date_time: task.due_date_time
    });
    setTaskModalOpen(true);
  };

  const confirmDeleteTask = (task) => {
    setTaskToDelete(task);
    setIsTaskDelete(true);
    setDeleteModalOpen(true);
  };

  const resetTaskForm = () => {
    setTaskFormData({
      title: "",
      description: "",
      status: "not_started",
      priority: "medium",
      assigned_to: "",
      due_date_time: ""
    });
    setIsEditing(false);
    setCurrentEditId(null);
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("token");
    try {
      let url = "";
      if (isTaskDelete) {
        url = `${API_BASE_URL}/task/delete/${taskToDelete.id}`;
      } else {
        url = `${API_BASE_URL}/milestone/delete/${milestoneToDelete.id}`;
      }

      const response = await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.status === true) {
        showToast("success", "Success", isTaskDelete ? "Task deleted" : "Milestone deleted");
        setDeleteModalOpen(false);
        if (isTaskDelete) fetchTasks(); else fetchMilestones();
      } else {
        showToast("error", "Error", response.data.message || "Delete failed");
      }
    } catch (error) {
      showToast("error", "Error", "Delete failed");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'on_hold': return 'warning';
      case 'not_started': return 'secondary';
      case 'pending': return 'warning'; // For milestone
      case 'planning': return 'primary'; // For milestone
      case 'cancelled': return 'danger';
      case 'urgent': return 'danger'; // Priority
      case 'high': return 'warning'; // Priority
      case 'medium': return 'info'; // Priority
      case 'low': return 'success'; // Priority
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Projects" breadcrumbItems={breadcrumbItems} />
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading project details...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Project Overview" breadcrumbItems={breadcrumbItems} />
          <Toast ref={toast} />

          <Row>
            {/* Project Details */}
            <Col lg={8}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-0">{project?.project_name}</h4>
                    <Badge color={getStatusBadge(project?.status)} className="font-size-12">
                      {project?.status}
                    </Badge>
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: project?.description }} />

                  <Row className="mt-4">
                    <Col md={6}>
                      <p className="text-muted mb-1">Start Date</p>
                      <h5>{project?.start_date}</h5>
                    </Col>
                    <Col md={6}>
                      <p className="text-muted mb-1">Deadline</p>
                      <h5>{project?.deadline}</h5>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Milestones Section */}
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Milestones</h5>
                  <Button color="primary" size="sm" onClick={() => { resetMilestoneForm(); setMilestoneModalOpen(true); }}>
                    Add Milestone
                  </Button>
                </CardHeader>
                <CardBody>
                  {milestoneLoading ? (
                    <div className="text-center"> <Spinner size="sm" /> </div>
                  ) : milestones.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-nowrap table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Due Date</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {milestones.map((milestone) => (
                            <tr key={milestone.id}>
                              <td>{milestone.title}</td>
                              <td>{milestone.due_date}</td>
                              <td>
                                <Badge color={getStatusBadge(milestone.status)}>
                                  {milestone.status}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button color="light" size="sm" onClick={() => editMilestone(milestone)}>
                                    <i className="mdi mdi-pencil" />
                                  </Button>
                                  <Button color="danger" size="sm" onClick={() => confirmDeleteMilestone(milestone)}>
                                    <i className="mdi mdi-trash-can" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted text-center">No milestones found.</p>
                  )}
                </CardBody>
              </Card>

              {/* Tasks Section */}
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Tasks</h5>
                  <Button color="primary" size="sm" onClick={() => { resetTaskForm(); setTaskModalOpen(true); }}>
                    Add Task
                  </Button>
                </CardHeader>
                <CardBody>
                  {taskLoading ? (
                    <div className="text-center"> <Spinner size="sm" /> </div>
                  ) : tasks.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-nowrap table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Assignee</th>
                            <th>Priority</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tasks.map((task) => (
                            <tr key={task.id}>
                              <td>{task.title}</td>
                              <td>{task.assigned_to_name || "Unassigned"}</td>
                              <td>
                                <Badge color={getStatusBadge(task.priority)}>
                                  {task.priority}
                                </Badge>
                              </td>
                              <td>
                                <Badge color={getStatusBadge(task.status)}>
                                  {task.status}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button color="light" size="sm" onClick={() => editTask(task)}>
                                    <i className="mdi mdi-pencil" />
                                  </Button>
                                  <Button color="danger" size="sm" onClick={() => confirmDeleteTask(task)}>
                                    <i className="mdi mdi-trash-can" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted text-center">No tasks found.</p>
                  )}
                </CardBody>
              </Card>

            </Col>

            {/* Sidebar */}
            <Col lg={4}>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Client Details</h5>
                </CardHeader>
                <CardBody>
                  {project?.client_id ? (
                    <ListGroup flush>
                      <ListGroupItem className="px-0 py-2 border-0">
                        <strong>Client:</strong> {project.client_name || "N/A"}
                      </ListGroupItem>
                      {/* You would fetch client details here if needed */}
                    </ListGroup>
                  ) : (
                    <p>No client information available.</p>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h5 className="mb-0">Budget Information</h5>
                </CardHeader>
                <CardBody>
                  <ListGroup flush>
                    <ListGroupItem className="px-0 py-2 d-flex justify-content-between align-items-center border-0">
                      <strong>Total Budget:</strong>
                      <span>${parseFloat(project?.budget || 0).toLocaleString()}</span>
                    </ListGroupItem>
                    <ListGroupItem className="px-0 py-2 d-flex justify-content-between align-items-center border-0">
                      <strong>Spent:</strong>
                      <span>${parseFloat(project?.spent_budget || 0).toLocaleString()}</span>
                    </ListGroupItem>
                    <ListGroupItem className="px-0 py-2 d-flex justify-content-between align-items-center border-0">
                      <strong>Remaining:</strong>
                      <span>
                        ${parseFloat((project?.budget || 0) - (project?.spent_budget || 0)).toLocaleString()}
                      </span>
                    </ListGroupItem>
                  </ListGroup>
                </CardBody>
              </Card>

              {/* Task Summary Card */}
              <Card className="mt-4">
                <CardHeader>
                  <h5 className="mb-0">Task Summary</h5>
                </CardHeader>
                <CardBody>
                  <ListGroup flush>
                    <ListGroupItem className="px-0 py-2 d-flex justify-content-between align-items-center border-0">
                      <span>Total Tasks</span>
                      <Badge color="primary" pill>{tasks.length}</Badge>
                    </ListGroupItem>
                    <ListGroupItem className="px-0 py-2 d-flex justify-content-between align-items-center border-0">
                      <span>Not Started</span>
                      <Badge color="secondary" pill>
                        {tasks.filter(task => task.status === "not_started").length}
                      </Badge>
                    </ListGroupItem>
                    <ListGroupItem className="px-0 py-2 d-flex justify-content-between align-items-center border-0">
                      <span>In Progress</span>
                      <Badge color="info" pill>
                        {tasks.filter(task => task.status === "in_progress").length}
                      </Badge>
                    </ListGroupItem>
                    <ListGroupItem className="px-0 py-2 d-flex justify-content-between align-items-center border-0">
                      <span>On Hold</span>
                      <Badge color="secondary" pill>
                        {tasks.filter(task => task.status === "on_hold").length}
                      </Badge>
                    </ListGroupItem>
                    <ListGroupItem className="px-0 py-2 d-flex justify-content-between align-items-center border-0">
                      <span>Completed</span>
                      <Badge color="success" pill>
                        {tasks.filter(task => task.status === "completed").length}
                      </Badge>
                    </ListGroupItem>
                  </ListGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Milestone Modal */}
        <Modal isOpen={milestoneModalOpen} toggle={() => setMilestoneModalOpen(!milestoneModalOpen)}>
          <ModalHeader toggle={() => setMilestoneModalOpen(!milestoneModalOpen)}>
            {isEditing ? "Edit Milestone" : "Add New Milestone"}
          </ModalHeader>
          <Form onSubmit={handleMilestoneSubmit}>
            <ModalBody>
              <FormGroup>
                <Label for="milestoneTitle">Title</Label>
                <Input
                  id="milestoneTitle"
                  name="title"
                  value={milestoneFormData.title}
                  onChange={handleMilestoneInputChange}
                  required
                />
              </FormGroup>

              <FormGroup >
                <Label for="milestoneDescription">Description</Label>
                <ReactQuill
                  theme="snow"
                  value={milestoneFormData.description}
                  onChange={handleMilestoneDescriptionChange}
                  style={{ height: "150px" }}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, false] }],
                      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      ['link', 'image'],
                      ['clean']
                    ]
                  }}
                />
              </FormGroup>
              <br />
              <br />
              <br />
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="milestoneStartDate">Start Date</Label>
                    <Input
                      id="milestoneStartDate"
                      name="start_date"
                      type="date"
                      value={milestoneFormData.start_date}
                      onChange={handleMilestoneInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="milestoneDueDate">Due Date</Label>
                    <Input
                      id="milestoneDueDate"
                      name="due_date"
                      type="date"
                      value={milestoneFormData.due_date}
                      onChange={handleMilestoneInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label for="milestoneStatus">Status</Label>
                <Input
                  id="milestoneStatus"
                  name="status"
                  type="select"
                  value={milestoneFormData.status}
                  onChange={handleMilestoneInputChange}
                >
                  <option value="planning">Planning</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Input>
              </FormGroup>
            </ModalBody>

            <ModalFooter>
              <Button color="light" onClick={() => setMilestoneModalOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                {isEditing ? "Update" : "Add"} Milestone
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* Task Modal */}
        <Modal isOpen={taskModalOpen} toggle={() => setTaskModalOpen(!taskModalOpen)}>
          <ModalHeader toggle={() => setTaskModalOpen(!taskModalOpen)}>
            {isEditing ? "Edit Task" : "Add New Task"}
          </ModalHeader>
          <Form onSubmit={handleTaskSubmit}>
            <ModalBody>
              <FormGroup>
                <Label for="taskTitle">Title</Label>
                <Input
                  id="taskTitle"
                  name="title"
                  value={taskFormData.title}
                  onChange={handleTaskInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="taskDescription">Description</Label>
                <ReactQuill
                  theme="snow"
                  value={taskFormData.description}
                  onChange={handleTaskDescriptionChange}
                  style={{ height: "150px" }}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, false] }],
                      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      ['link', 'image'],
                      ['clean']
                    ]
                  }}
                />
              </FormGroup>
              <br />
              <br />
              <br />
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="taskStatus">Status</Label>
                    <Input
                      id="taskStatus"
                      name="status"
                      type="select"
                      value={taskFormData.status}
                      onChange={handleTaskInputChange}
                    >
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="on_hold">On Hold</option>
                      <option value="completed">Completed</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="taskPriority">Priority</Label>
                    <Input
                      id="taskPriority"
                      name="priority"
                      type="select"
                      value={taskFormData.priority}
                      onChange={handleTaskInputChange}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="taskAssignedTo">Assign To</Label>
                    <Input
                      id="taskAssignedTo"
                      name="assigned_to"
                      type="select"
                      value={taskFormData.assigned_to}
                      onChange={handleTaskInputChange}
                      disabled={staffLoading}
                    >
                      <option value="">Select Staff</option>
                      {staff.map((staffMember) => (
                        <option key={staffMember.user_id} value={staffMember.user_id}>
                          {staffMember.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="taskDueDateTime">Due Date & Time</Label>
                    <Input
                      id="taskDueDateTime"
                      name="due_date_time"
                      type="datetime-local"
                      value={taskFormData.due_date_time}
                      onChange={handleTaskInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button color="light" onClick={() => setTaskModalOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                {isEditing ? "Update" : "Add"} Task
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(!deleteModalOpen)}>
          <ModalHeader toggle={() => setDeleteModalOpen(!deleteModalOpen)}>
            Confirm Delete
          </ModalHeader>
          <ModalBody>
            {isTaskDelete ? (
              <p>Are you sure you want to delete the task "{taskToDelete?.title}"? This action cannot be undone.</p>
            ) : (
              <p>Are you sure you want to delete the milestone "{milestoneToDelete?.title}"? This action cannot be undone.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDelete}>
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default View;